// Instagram Graph API hard cap on media `caption` field. Not increasable.
export const MAX_CAPTION_LENGTH = 2200;

// Item 7 (2026-06-04) budget split of the 2200 IG cap, after removing
// "Event Referral Caption" (eventCaption2) from the IG composition — it's now
// a WhatsApp/Telegram-share-only field. Hard caps enforced in admin + apps:
//   - Participation post caption (eventCaption1): ≤ 1750
//   - Participant's own text: ≤ 200
//   - Hashtags: ≤ 10 total (≤5 admin + ≤5 user; user block ≤100 chars)
//   - "Use my NNexus code: XXXX" line: auto-reserved (~40)
export const CAPTION1_MAX = 1750;
export const PARTICIPANT_TEXT_MAX = 200;
export const MAX_ADMIN_HASHTAGS = 5;
export const MAX_USER_HASHTAGS = 5;
export const MAX_TOTAL_HASHTAGS = 10;
export const USER_HASHTAG_BLOCK_MAX = 100;

// Mirrors the concatenation the user app does in
// userapp/Namasvi-User-App/lib/data/controllers/meta_controller.dart
//   finalCaption = eventCaption1 + "\n" + userText
//                + "\n\n " + hashtags(joined as #tag with spaces)
//                + "\n\nUse my referral code <REFCODE>"
//                + "\n\n" + (eventCaption2 || "to grab some bonus points! Don't miss out!")
// Sponsor handles ride along as user_tags, not caption — so they don't count.
//
// Returns an object with breakdown so callers can render counters.
export function estimateCaptionBudget({
  eventCaption1 = "",
  eventCaption2 = "",
  hashtags = [],
  userTextLen = 0,
  refCodeLen = 10, // typical IG username length budget
} = {}) {
  const cap1 = (eventCaption1 || "").length;
  // Item 7: eventCaption2 ("Event Referral Caption") is NO LONGER part of the
  // IG caption (WhatsApp/Telegram-share field only), so it's excluded from
  // the IG budget math. Param kept for backward-compat with callers.
  void eventCaption2;
  const cap2 = 0;

  const tags = Array.isArray(hashtags) ? hashtags : [];
  const hashtagBlock = tags.length
    ? tags.reduce((acc, t) => acc + 1 /* space */ + 1 /* # */ + String(t).length, 2 /* "\n\n" */)
    : 0;

  // "\n\nUse my NNexus code " = ~22 chars, plus refCodeLen
  const refBlock = 22 + Math.max(0, refCodeLen);

  // joiners: cap1 + "\n" (1) + userText + hashtagBlock (incl leading \n\n) + refBlock (incl \n\n)
  const adminOverhead = cap1 + 1 + hashtagBlock + refBlock;
  const total = adminOverhead + Math.max(0, userTextLen);

  return {
    cap1Len: cap1,
    cap2Len: cap2, // always 0 now (not in IG); kept so callers don't break
    hashtagBlockLen: hashtagBlock,
    refBlockLen: refBlock,
    adminOverhead,
    totalEstimated: total,
    userBudgetRemaining: Math.max(0, MAX_CAPTION_LENGTH - adminOverhead),
    max: MAX_CAPTION_LENGTH,
    // Caption1 hard cap (Item 7) — admin form blocks save above this.
    cap1Over: cap1 > CAPTION1_MAX,
    cap1Max: CAPTION1_MAX,
    isOver: total > MAX_CAPTION_LENGTH || cap1 > CAPTION1_MAX,
    isCritical: total > MAX_CAPTION_LENGTH - 100,
  };
}

// Tone-based helper for textarea counter chip color
export function captionLengthTone(len, softLimit, hardLimit = MAX_CAPTION_LENGTH) {
  if (len >= hardLimit) return "red";
  if (len >= softLimit) return "yellow";
  return "gray";
}
